import React from "react";
import { api } from "../../lib/api";
import { AlertCircle, Download, Loader2, Settings2, Sparkles, Trash2 } from "lucide-react";
import { BrandProfileForm } from "./BrandProfileForm";
import { PlanItemCard, FORMAT_LABELS } from "./PlanItemCard";

const PLURALS = { post: "posts", carousel: "carrousels", reel: "reels", story: "stories" };

const nextMonday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
};

const GenerateForm = ({ onCreated }) => {
  const [week, setWeek] = React.useState(nextMonday());
  const [count, setCount] = React.useState("");
  const [focus, setFocus] = React.useState("");
  const [ai, setAi] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/marketing/plans/generate", { week_start: week, focus, ai_backgrounds: ai, ...(count ? { count: Number(count) } : {}) });
      onCreated(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Impossible de lancer la génération."); }
    finally { setBusy(false); }
  };
  const field = "bg-black/40 border border-white/10 rounded px-3 py-2 text-sm";
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="planner-generate-form">
      <div className="flex flex-wrap gap-4 items-end">
        <label className="text-xs text-text-cream/60">Semaine du (lundi)<br/>
          <input type="date" value={week} onChange={(e) => setWeek(e.target.value)} className={`${field} mt-1`} data-testid="planner-week-input"/></label>
        <label className="text-xs text-text-cream/60">Contenus<br/>
          <select value={count} onChange={(e) => setCount(e.target.value)} className={`${field} mt-1`} data-testid="planner-count-select">
            <option value="">Selon mon profil</option>{[5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="text-xs text-text-cream/60 flex-1 min-w-[240px]">Consigne de la semaine (facultatif)<br/>
          <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="ex. Lancer le Shooting de Noël" className={`${field} mt-1 w-full`} data-testid="planner-focus-input"/></label>
        <label className="text-xs text-text-cream/70 flex items-center gap-2 pb-2">
          <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} data-testid="planner-ai-toggle"/> Fonds IA (max. 3, sans personne)</label>
        <button onClick={submit} disabled={busy} className="btn btn-primary" data-testid="planner-generate-btn">
          {busy ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16}/>} Générer la semaine
        </button>
      </div>
      {err && <p role="alert" className="mt-3 text-sm text-red-300" data-testid="planner-generate-error">{err}</p>}
    </div>
  );
};

const PlanView = ({ plan, onItem, onDelete }) => {
  const counts = plan.items.reduce((a, i) => ({ ...a, [i.format]: (a[i.format] || 0) + 1 }), {});
  const approved = plan.items.filter((i) => i.status === "approved").length;
  const download = async () => {
    const r = await api.get(`/admin/marketing/plans/${plan.id}/export`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `planning_${plan.week_start}.zip`; a.click();
  };
  return (
    <section className="space-y-4" data-testid={`plan-${plan.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="font-serif text-2xl">Semaine du {plan.week_label}</h4>
        <span className="text-xs text-text-cream/50" data-testid="plan-summary">
          {[`${plan.items.length} contenus`, ...Object.entries(counts).map(([k, n]) => `${n} ${n > 1 ? PLURALS[k] : FORMAT_LABELS[k].toLowerCase()}`), `${approved} validé${approved > 1 ? "s" : ""}`].join(" · ")}
        </span>
        <div className="ml-auto flex gap-2">
          <button onClick={download} disabled={plan.status !== "ready"} className="btn btn-ghost text-xs" data-testid="plan-export-btn"><Download size={13}/> Exporter (.zip)</button>
          <button onClick={onDelete} className="btn btn-ghost text-xs text-red-300" data-testid="plan-delete-btn"><Trash2 size={13}/> Supprimer</button>
        </div>
      </div>
      {plan.focus && <p className="text-sm text-text-cream/60">Consigne : {plan.focus}</p>}
      {plan.status === "generating" && (
        <div className="p-4 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-100 text-sm flex gap-2 items-center" data-testid="plan-generating">
          <Loader2 className="animate-spin" size={16}/> Claude rédige la semaine, puis les fonds IA sont créés (1 à 3 minutes). Vous pouvez quitter la page.
        </div>
      )}
      {plan.status === "failed" && <div role="alert" className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm flex gap-2" data-testid="plan-failed"><AlertCircle size={16}/> {plan.error}</div>}
      {plan.items.map((item) => <PlanItemCard key={item.id} planId={plan.id} item={item} onChange={(it) => onItem(plan.id, it)}/>)}
    </section>
  );
};

export const ContentPlanner = () => {
  const [plans, setPlans] = React.useState([]);
  const [activeId, setActiveId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showBrand, setShowBrand] = React.useState(false);
  const load = React.useCallback(() => api.get("/admin/marketing/plans").then((r) => {
    setPlans(r.data.items);
    setActiveId((id) => id || r.data.items[0]?.id || null);
  }).finally(() => setLoading(false)), []);
  React.useEffect(() => { load(); }, [load]);
  const generating = plans.some((p) => p.status === "generating");
  React.useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [generating, load]);
  const active = plans.find((p) => p.id === activeId);
  const onItem = (planId, item) => setPlans((ps) => ps.map((p) => (p.id !== planId ? p : { ...p, items: p.items.map((i) => (i.id === item.id ? item : i)).sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)) })));
  const onDelete = async () => {
    if (!window.confirm("Supprimer ce planning ?")) return;
    await api.delete(`/admin/marketing/plans/${activeId}`);
    setActiveId(null); load();
  };
  return (
    <div className="space-y-6" data-testid="content-planner-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-serif text-2xl">Créateur de contenu IA</h3>
          <p className="text-sm text-text-cream/50 mt-1 max-w-2xl font-light">
            5 à 7 contenus par semaine (posts, carrousels, Reels, stories), rédigés par Claude Sonnet 5.5 selon votre profil de marque.
            Vos vraies photos montrent vos séances ; l'IA ne crée que des fonds graphiques, jamais de personnes. Rien n'est publié automatiquement.
          </p>
        </div>
        <button onClick={() => setShowBrand((s) => !s)} className="btn btn-ghost" data-testid="brand-toggle-btn"><Settings2 size={15}/> Ma marque</button>
      </div>
      {showBrand && <BrandProfileForm onClose={() => setShowBrand(false)}/>}
      <GenerateForm onCreated={(p) => { setPlans((ps) => [p, ...ps]); setActiveId(p.id); }}/>
      {loading && <p className="text-sm text-text-cream/50">Chargement…</p>}
      {!loading && plans.length === 0 && <div className="p-10 text-center rounded-xl border border-dashed border-white/10 text-text-cream/50" data-testid="planner-empty">Aucun planning pour l'instant. Choisissez une semaine et cliquez sur « Générer la semaine ».</div>}
      {plans.length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="plan-list">
          {plans.map((p) => (
            <button key={p.id} onClick={() => setActiveId(p.id)} data-testid={`plan-tab-${p.id}`}
              className={`px-4 py-2 rounded-full text-xs border ${p.id === activeId ? "border-accent-deep text-accent-light" : "border-white/15 text-text-cream/60"}`}>
              {p.week_label}{p.status === "generating" ? " · en cours" : p.status === "failed" ? " · échec" : ""}
            </button>
          ))}
        </div>
      )}
      {active && <PlanView plan={active} onItem={onItem} onDelete={onDelete}/>}
    </div>
  );
};
